/* =========================================================================
   ARC GYM WEAR
   Carousel, variant resolution, accordion. Vanilla — no libraries, no build.
   Motion budget lives in design.md §8; nothing here animates on scroll.
   ========================================================================= */

(function () {
  'use strict';

  var reducedQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  function prefersReduced() {
    return reducedQuery.matches;
  }

  // There is deliberately no hover-to-pause. Autoplay runs regardless of where
  // the pointer is; the Pause button and picking a colourway are the only ways
  // a visitor stops it. Hovering used to halt the carousel and it read as the
  // thing being broken — the pointer often just rests over the page.

  // :focus-visible, not :focus — clicking the pause button with a mouse leaves
  // it focused, and treating that as "a keyboard user is reading this" meant
  // pressing Play appeared to do nothing at all. This never fires for mouse or
  // touch, only for genuine keyboard navigation.
  function isKeyboardFocused(root) {
    try {
      return !!root.querySelector(':focus-visible');
    } catch (e) {
      return false;
    }
  }

  /* ---------------------------------------------------------------------
     Carousel
     --------------------------------------------------------------------- */

  function ArcCarousel(root) {
    this.root = root;
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-arc-slide]'));
    this.triggers = Array.prototype.slice.call(root.querySelectorAll('[data-arc-goto]'));
    this.segs = Array.prototype.slice.call(root.querySelectorAll('[data-arc-seg]'));
    this.toggleBtn = root.querySelector('[data-arc-toggle]');
    this.toggleLabel = root.querySelector('[data-arc-toggle-label]');
    this.nameEl = root.querySelector('[data-arc-name]');
    this.indexEl = root.querySelector('[data-arc-index]');
    this.blurbEl = root.querySelector('[data-arc-blurb]');

    this.count = this.slides.length;
    if (this.count === 0) return;

    this.dwell = parseInt(root.dataset.dwell, 10) || 5000;
    this.index = 0;
    this.timer = null;
    this.running = false;
    this.elapsed = 0;
    this.lastTick = 0;

    // Autoplay is off entirely for reduced-motion visitors (design.md §8).
    this.playing = root.dataset.autoplay !== 'false' && !prefersReduced();

    this.isShowcase = root.hasAttribute('data-arc-showcase');
    if (this.isShowcase) {
      this.variants = new ArcVariants(root);
    }

    this.bind();
    this.goto(0, { user: false });
    this.update();
    this.syncToggle();
  }

  ArcCarousel.prototype.bind = function () {
    var self = this;

    this.triggers.forEach(function (btn) {
      btn.addEventListener('click', function () {
        // A deliberate pick holds that colourway — it does not resume.
        self.playing = false;
        self.update();
        self.syncToggle();
        self.goto(parseInt(btn.dataset.arcGoto, 10), { user: true });
      });
    });

    if (this.toggleBtn) {
      this.toggleBtn.addEventListener('click', function () {
        self.playing ? self.pause() : self.resume();
      });
    }

    // These listeners only nudge the carousel to re-evaluate; none of them
    // records state and none of them arms or disarms the timer. shouldRun()
    // reads focus straight from the DOM, so an event that never arrives — a
    // focusout swallowed by a cross-document focus change, say — costs one
    // tick, not the rest of the session.
    self.root.addEventListener('focusin', function () {
      self.update();
    });

    self.root.addEventListener('focusout', function () {
      self.update();
    });

    // Stop burning timers while the tab is hidden, and re-arm on return.
    document.addEventListener('visibilitychange', function () {
      self.update();
    });

    // Coming back from another window can restore focus somewhere the carousel
    // never saw it move to.
    window.addEventListener('focus', function () {
      self.update();
    });

    // Honour a mid-session change to the reduced-motion setting.
    var onReducedChange = function () {
      if (prefersReduced()) {
        self.playing = false;
        self.stop();
        self.syncToggle();
        self.paintProgress();
      }
    };
    if (reducedQuery.addEventListener) {
      reducedQuery.addEventListener('change', onReducedChange);
    } else if (reducedQuery.addListener) {
      reducedQuery.addListener(onReducedChange);
    }
  };

  // Single source of truth for whether the carousel should be advancing.
  // Nothing is cached — every condition is read at the moment it is asked for,
  // and no event handler arms or disarms the interval directly.
  //
  // Deliberately absent: hover. Autoplay does not care where the pointer is.
  ArcCarousel.prototype.shouldRun = function () {
    return (
      this.playing &&
      !document.hidden &&
      !prefersReduced() &&
      !isKeyboardFocused(this.root)
    );
  };

  // A short heartbeat that always runs, rather than one interval armed at the
  // dwell length and disarmed by event handlers. Advancing is decided inside
  // the tick from conditions read live, so the carousel re-checks itself
  // several times a second and cannot end up permanently stopped by a state
  // flag that never got cleared. The tick is idle work — no layout, no paint —
  // whenever nothing has changed.
  ArcCarousel.TICK = 200;

  ArcCarousel.prototype.update = function () {
    var self = this;
    var want = this.shouldRun();

    if (want !== this.running) {
      this.running = want;
      this.elapsed = 0;
      this.lastTick = Date.now();
      this.paintProgress();
    }

    if (!this.timer) {
      this.timer = setInterval(function () {
        self.tick();
      }, ArcCarousel.TICK);
    }
  };

  ArcCarousel.prototype.tick = function () {
    var want = this.shouldRun();

    if (want !== this.running) {
      this.running = want;
      this.elapsed = 0;
      this.lastTick = Date.now();
      this.paintProgress();
      return;
    }

    if (!this.running) return;

    var now = Date.now();
    // A laptop waking from sleep reports an enormous delta; clamp it so the
    // carousel resumes where it was instead of skipping ahead several slides.
    var delta = Math.min(now - this.lastTick, ArcCarousel.TICK * 4);
    this.lastTick = now;
    this.elapsed += delta;

    if (this.elapsed >= this.dwell) {
      this.elapsed = 0;
      this.goto((this.index + 1) % this.count, { user: false });
    }
  };

  ArcCarousel.prototype.start = function () {
    this.playing = true;
    this.update();
  };

  ArcCarousel.prototype.stop = function () {
    this.running = false;
    this.elapsed = 0;
    this.paintProgress();
  };

  ArcCarousel.prototype.destroy = function () {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  };

  ArcCarousel.prototype.pause = function () {
    this.playing = false;
    this.update();
    this.syncToggle();
  };

  ArcCarousel.prototype.resume = function () {
    this.playing = true;
    this.update();
    this.syncToggle();
  };

  ArcCarousel.prototype.syncToggle = function () {
    if (!this.toggleBtn) return;
    this.toggleBtn.setAttribute('aria-pressed', String(this.playing));
    var word = this.playing ? 'Pause' : 'Play';
    if (this.toggleLabel) this.toggleLabel.textContent = word;
    this.toggleBtn.setAttribute('aria-label', word + ' colourway autoplay');
  };

  ArcCarousel.prototype.goto = function (i, opts) {
    if (i < 0 || i >= this.count) return;
    this.index = i;

    // Every arrival at a slide starts its dwell from zero, however it happened.
    this.elapsed = 0;
    this.lastTick = Date.now();

    this.slides.forEach(function (slide, idx) {
      var on = idx === i;
      slide.dataset.active = String(on);
      if (on) {
        slide.removeAttribute('aria-hidden');
      } else {
        slide.setAttribute('aria-hidden', 'true');
      }
    });

    this.triggers.forEach(function (btn, idx) {
      var on = idx === i;
      if (btn.hasAttribute('role') && btn.getAttribute('role') === 'tab') {
        btn.setAttribute('aria-selected', String(on));
      } else {
        btn.setAttribute('aria-pressed', String(on));
      }
    });

    var active = this.triggers[i];
    if (active && this.nameEl) {
      var name = active.dataset.arcLabel;
      if (name) this.nameEl.textContent = name;
    }

    if (this.indexEl) {
      this.indexEl.textContent =
        ('0' + (i + 1)).slice(-2) + ' / ' + ('0' + this.count).slice(-2);
    }

    if (this.blurbEl) {
      var slide = this.slides[i];
      var blurb = slide && slide.dataset.blurb;
      if (blurb) this.blurbEl.textContent = blurb;
    }

    this.paintProgress();

    if (this.variants && active) {
      this.variants.setColor(active.dataset.arcOptionValue || '');
    }

    if (opts && opts.user) this.announce();
  };

  ArcCarousel.prototype.announce = function () {
    if (!this.nameEl) return;
    this.nameEl.setAttribute('aria-live', 'polite');
  };

  ArcCarousel.prototype.paintProgress = function () {
    var self = this;
    if (!this.segs.length) return;

    this.segs.forEach(function (seg, idx) {
      var fill = seg.querySelector('.progress__fill');
      if (!fill) return;

      if (idx !== self.index) {
        fill.style.transition = 'none';
        seg.dataset.state = 'idle';
        return;
      }

      // Reset, force a reflow, then re-arm so the fill restarts every cycle.
      fill.style.transition = 'none';
      seg.dataset.state = 'idle';
      void fill.offsetWidth;

      if (self.running && !prefersReduced()) {
        fill.style.transition = '';
        seg.dataset.state = 'active';
      } else {
        seg.dataset.state = 'paused';
      }
    });
  };

  /* ---------------------------------------------------------------------
     Variant resolution

     Maps the selected colourway + size onto a real Shopify variant and keeps
     the form's <select name="id"> in sync. That select is the only thing the
     cart actually reads, and it works on its own without JavaScript.
     --------------------------------------------------------------------- */

  function ArcVariants(root) {
    this.root = root;
    this.select = root.querySelector('[data-arc-variant-select]');
    this.dataEl = root.querySelector('[data-arc-variants]');
    this.submit = root.querySelector('[data-arc-submit]');
    this.submitLabel = root.querySelector('[data-arc-submit-label]');
    this.colorNameEl = root.querySelector('[data-arc-color-name]');
    this.sizeNoteEl = root.querySelector('[data-arc-size-note]');
    this.sizeBtns = Array.prototype.slice.call(root.querySelectorAll('[data-arc-size]'));
    this.colorBtns = Array.prototype.slice.call(root.querySelectorAll('[data-arc-color]'));

    // Price appears in more than one place on the product page (panel + the
    // sticky bar), so this is deliberately a list.
    this.priceEls = Array.prototype.slice.call(
      document.querySelectorAll('[data-arc-price]')
    );

    // The struck-through original sits beside each live price. It is a
    // separate element rather than part of the price text so that rewriting
    // the price cannot wipe it — and so a variant priced without a discount
    // hides its own strike rather than showing a stale one.
    this.compareEls = Array.prototype.slice.call(
      document.querySelectorAll('[data-arc-compare]')
    );
    this.badgeEls = Array.prototype.slice.call(
      document.querySelectorAll('[data-arc-badge]')
    );
    this.barSummary = document.querySelector('[data-arc-bar-summary]');
    this.mediaViews = Array.prototype.slice.call(
      document.querySelectorAll('[data-arc-media]')
    );

    // Showcase mode: instead of adding to cart, the button links through to the
    // product page with the chosen variant preselected.
    this.buyLink = root.querySelector('[data-arc-buy-link]');
    this.productUrl = root.dataset.productUrl || '';

    this.colorIndex = parseInt(root.dataset.colorIndex, 10);
    this.sizeIndex = parseInt(root.dataset.sizeIndex, 10);
    if (isNaN(this.colorIndex)) this.colorIndex = -1;
    if (isNaN(this.sizeIndex)) this.sizeIndex = -1;

    if (!this.dataEl) return;
    if (!this.select && !this.buyLink) return;

    try {
      this.variants = JSON.parse(this.dataEl.textContent).variants || [];
    } catch (e) {
      this.variants = [];
      return;
    }

    // JS is running, so the raw select is redundant — hide it visually but
    // keep it in the form and in the accessibility tree.
    if (this.select) this.select.classList.add('visually-hidden');

    // One entry per option position, whatever the options are called. This is
    // what variants are matched on, so a product with three options resolves
    // as correctly as one with two — the old colour+size pair silently ignored
    // a third option and could select the wrong variant.
    //
    // The product page marks every button with data-arc-opt-index. The
    // showcase still renders the older data-arc-color buttons only, so those
    // are folded in at the colour position. Buttons carrying both are counted
    // once, from the explicit index.
    this.optBtns = [];
    this.lastChanged = -1;
    var seen = [];
    var self0 = this;

    Array.prototype.forEach.call(
      root.querySelectorAll('[data-arc-opt-index]'),
      function (el) {
        var idx = parseInt(el.dataset.arcOptIndex, 10);
        if (isNaN(idx)) return;
        self0.optBtns.push({ el: el, index: idx, value: el.dataset.arcOptValue });
        seen.push(el);
      }
    );

    function foldLegacy(btns, key, index) {
      if (index < 0) return;
      btns.forEach(function (el) {
        if (seen.indexOf(el) !== -1) return;
        self0.optBtns.push({ el: el, index: index, value: el.dataset[key] });
        seen.push(el);
      });
    }
    foldLegacy(this.colorBtns, 'arcColor', this.colorIndex);
    foldLegacy(this.sizeBtns, 'arcSize', this.sizeIndex);

    // Seed from whatever the server rendered as pressed, so ?variant=… lands
    // on the right combination. Any option the markup did not press falls back
    // to its first value.
    this.selected = [];
    this.optBtns.forEach(function (b) {
      if (self0.selected[b.index] === undefined) self0.selected[b.index] = '';
      if (b.el.getAttribute('aria-pressed') === 'true') {
        self0.selected[b.index] = b.value;
      }
    });
    this.optBtns.forEach(function (b) {
      if (!self0.selected[b.index]) self0.selected[b.index] = b.value;
    });

    this.syncNamed();

    this.baseLabel = this.submitLabel
      ? this.submitLabel.textContent.trim()
      : 'Buy';

    this.bind();
    this.sync();
  }

  // this.color and this.size are conveniences for the parts that genuinely
  // are about colour and size — the curated gallery, the colourway label, the
  // sticky bar summary. They are derived from the option tuple, never the
  // other way round, so nothing can drift out of step with the real selection.
  // Is this value buyable in any combination at all? Distinct from the
  // contextual "sold out given what else is selected" used for styling.
  ArcVariants.prototype.hasAnyAvailable = function (index, value) {
    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i];
      if (v.available && v.options[index] === value) return true;
    }
    return false;
  };

  // Distinct values offered for one option position, in the order the admin
  // arranged them.
  ArcVariants.prototype.valuesFor = function (index) {
    var out = [];
    this.optBtns.forEach(function (b) {
      if (b.index === index && out.indexOf(b.value) === -1) out.push(b.value);
    });
    return out;
  };

  ArcVariants.prototype.syncNamed = function () {
    this.color = this.colorIndex >= 0 ? this.selected[this.colorIndex] || '' : '';
    this.size = this.sizeIndex >= 0 ? this.selected[this.sizeIndex] || '' : '';
  };

  ArcVariants.prototype.bind = function () {
    var self = this;

    this.optBtns.forEach(function (b) {
      b.el.addEventListener('click', function () {
        // Only a value that is unavailable in EVERY combination is refused.
        // Judging by the current combination instead would wall the visitor in:
        // a colourway sold out in the size they happen to have selected became
        // unclickable, so there was no way to reach it at all.
        if (!self.hasAnyAvailable(b.index, b.value)) return;
        self.lastChanged = b.index;
        self.selected[b.index] = b.value;
        self.syncNamed();
        self.sync();
      });
    });

    // Sticky bar submits the same form rather than owning its own state.
    var barAdd = document.querySelector('[data-arc-bar-add]');
    var form = root_form(this.root);
    if (barAdd && form) {
      barAdd.addEventListener('click', function () {
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.submit();
      });
    }
  };

  function root_form(root) {
    return root.querySelector('[data-arc-form]');
  }

  ArcVariants.prototype.setColor = function (value) {
    if (this.colorIndex >= 0) this.selected[this.colorIndex] = value;
    this.syncNamed();
    this.sync();
  };

  // Matches on the whole option tuple. Pass index/value to ask "what would be
  // selected if this one button were pressed?" — which is how each button
  // works out whether it leads anywhere in stock.
  ArcVariants.prototype.match = function (index, value) {
    var sel = this.selected.slice();
    if (typeof index === 'number' && index >= 0) sel[index] = value;

    return this.variants.filter(function (v) {
      for (var i = 0; i < sel.length; i++) {
        if (!sel[i]) continue;
        if (v.options[i] !== sel[i]) return false;
      }
      return true;
    })[0];
  };

  ArcVariants.prototype.sync = function () {
    var self = this;
    if (!this.variants || !this.variants.length) return;

    var variant = this.match();

    // The chosen combination is gone. Hold whichever option the visitor is
    // most likely to care about — the colour — and move the others to
    // something in stock, rather than leaving a dead Add to cart button.
    if (!variant || !variant.available) {
      for (var oi = 0; oi < this.selected.length; oi++) {
        // Never move the colour, and never move the option the visitor just
        // chose — undoing their click would make the button look broken.
        if (oi === this.colorIndex || oi === this.lastChanged) continue;
        var candidates = this.valuesFor(oi);
        for (var k = 0; k < candidates.length; k++) {
          var test = this.match(oi, candidates[k]);
          if (test && test.available) {
            this.selected[oi] = candidates[k];
            break;
          }
        }
      }
      this.syncNamed();
      variant = this.match();
    }

    // Mark every option button: pressed if it is the current choice, sold out
    // if choosing it would land on a variant that cannot be bought. Applies to
    // all options, not just size — a colourway that is completely out of stock
    // now says so instead of looking selectable.
    this.optBtns.forEach(function (b) {
      var v = self.match(b.index, b.value);
      var soldOut = !v || !v.available;
      b.el.dataset.soldOut = String(soldOut);
      b.el.setAttribute('aria-pressed', String(self.selected[b.index] === b.value));
      if (soldOut) {
        b.el.setAttribute('aria-disabled', 'true');
        b.el.title = 'Sold out';
      } else {
        b.el.removeAttribute('aria-disabled');
        b.el.removeAttribute('title');
      }
    });

    if (variant) {
      if (this.select) this.select.value = String(variant.id);

      // Showcase: carry colour + size through to the product page rather than
      // dropping the visitor straight into checkout.
      if (this.buyLink && this.productUrl) {
        this.buyLink.href = this.productUrl + '?variant=' + variant.id;
      }

      if (variant.price) {
        this.priceEls.forEach(function (el) {
          el.textContent = variant.price;
        });
      }

      // Keep the struck original and the discount tag in step with the price.
      // A null compareAt means this variant is not discounted, so both are
      // hidden rather than left showing a figure that no longer applies —
      // which is what a colourway priced differently to the rest would do.
      this.compareEls.forEach(function (el) {
        var value = el.querySelector('[data-arc-compare-value]');
        if (variant.compareAt) {
          if (value) value.textContent = variant.compareAt;
          el.hidden = false;
        } else {
          el.hidden = true;
        }
      });

      // The tag carries a percentage, so revealing it is not enough — its text
      // has to be rewritten too, or a variant discounted by a different amount
      // shows the previous variant's figure next to its own prices.
      this.badgeEls.forEach(function (el) {
        if (variant.badge) {
          el.textContent = variant.badge;
          el.hidden = false;
        } else {
          el.hidden = !variant.compareAt;
        }
      });

      // Swap the gallery to the media attached to this variant, when there is
      // one. Products without per-variant media simply keep the current view.
      if (variant.mediaId && this.mediaViews.length) {
        var found = false;
        this.mediaViews.forEach(function (view) {
          if (String(view.dataset.arcMedia) === String(variant.mediaId)) found = true;
        });
        if (found) showMedia(String(variant.mediaId));
      }
    }

    // Thumbnails tagged with a colourway (from the image's alt text, see
    // product-main.liquid) show only while that colourway is selected, so a
    // six-image product reads as "front and back of the shirt you picked"
    // rather than a strip of every colour. Untagged thumbnails always show.
    if (this.color) {
      var color = this.color;
      this.root.querySelectorAll('[data-arc-thumb][data-arc-thumb-color]').forEach(function (thumb) {
        var tag = thumb.dataset.arcThumbColor;
        thumb.hidden = !!tag && tag !== color;
      });
    }

    if (this.colorNameEl && this.color) this.colorNameEl.textContent = this.color;

    var available = !!(variant && variant.available);

    if (this.sizeNoteEl) {
      this.sizeNoteEl.textContent = available
        ? ''
        : 'Sold out — choose another option';
    }

    // The selection as words, for the sticky bar and the Add to cart label.
    // A bare value is ambiguous when several options share a value set — the
    // 3-in-1 Bundle has three size pickers, and "S · M · L" says nothing about
    // which shirt is which. product-main.liquid stamps a short prefix
    // (data-arc-opt-short, e.g. "Black") on those buttons; where it exists the
    // bit reads "Black S". The colour value is lower-cased on the button label
    // only, as it always was ("phantom black · M").
    var shorts = {};
    this.optBtns.forEach(function (b) {
      if (b.el.dataset.arcOptShort) shorts[b.index] = b.el.dataset.arcOptShort;
    });
    var bits = [];
    var labelBits = [];
    this.selected.forEach(function (v, i) {
      if (!v) return;
      var bit = shorts[i] ? shorts[i] + ' ' + v : v;
      bits.push(bit);
      labelBits.push(i === self.colorIndex ? bit.toLowerCase() : bit);
    });

    if (this.barSummary) {
      this.barSummary.textContent = bits.join(' · ');
    }

    if (this.submit) {
      this.submit.disabled = !available;
      this.submit.setAttribute('aria-disabled', String(!available));
    }

    // An anchor can't be disabled; mark it and let it still reach the product
    // page, where the visitor can pick a size that is in stock.
    if (this.buyLink) {
      this.buyLink.setAttribute('aria-disabled', String(!available));
      this.buyLink.classList.toggle('btn--muted', !available);
    }

    if (this.submitLabel) {
      if (!available) {
        this.submitLabel.textContent = 'Sold out';
      } else {
        this.submitLabel.textContent = [this.baseLabel].concat(labelBits).join(' · ') + ' →';
      }
    }

    // Keep the sticky mobile bar honest.
    var barName = document.querySelector('[data-arc-buybar-name]');
    var barPrice = document.querySelector('[data-arc-buybar-price]');
    if (barName && this.color) barName.textContent = this.color;
    if (barPrice && variant && variant.price) barPrice.textContent = variant.price;
  };

  /* ---------------------------------------------------------------------
     Product media — thumbnails and variant-driven switching
     --------------------------------------------------------------------- */

  function showMedia(id) {
    var views = document.querySelectorAll('[data-arc-media]');
    var thumbs = document.querySelectorAll('[data-arc-thumb]');

    Array.prototype.forEach.call(views, function (view) {
      var on = String(view.dataset.arcMedia) === String(id);
      view.hidden = !on;

      // Stop a video that has been scrolled away from.
      if (!on) {
        var vid = view.querySelector('video');
        if (vid && !vid.paused) vid.pause();
      }
    });

    Array.prototype.forEach.call(thumbs, function (thumb) {
      thumb.setAttribute(
        'aria-current',
        String(String(thumb.dataset.arcThumb) === String(id))
      );
    });
  }

  function initMedia() {
    var thumbs = document.querySelectorAll('[data-arc-thumb]');

    // No early return on an empty thumb list: a product with a single image
    // renders no thumbnails at all, and its one view still has to be unhidden
    // or the stage comes up blank.
    Array.prototype.forEach.call(thumbs, function (thumb) {
      thumb.addEventListener('click', function () {
        if (thumb.dataset.arcThumb) showMedia(thumb.dataset.arcThumb);
      });
    });

    // ArcVariants has already run by now and, when the selected variant has
    // an image attached, has unhidden it — a product page opened from the
    // showcase's Buy button lands on the chosen colourway, not on whatever
    // happens to be first in product.media. Only fall back to the first view
    // when nothing is showing yet.
    if (document.querySelector('[data-arc-media]:not([hidden])')) return;

    var first = document.querySelector('[data-arc-media]');
    if (first) showMedia(first.dataset.arcMedia);
  }

  /* ---------------------------------------------------------------------
     Cart quantity

     The + / − controls are ordinary links to /cart/change, so the cart works
     with JavaScript off. With JavaScript on, waiting for that round trip
     before the number moves reads as a dead button and people click again.
     Here the number changes on the click and the server is told afterwards,
     with the response treated as the authority once it lands.
     --------------------------------------------------------------------- */

  // Mirrors Shopify's money_format so an optimistic total looks identical to
  // the one Liquid rendered. Amounts are in cents, as everywhere in the API.
  function formatMoney(cents, format) {
    var value = Math.max(0, Math.round(cents)) / 100;

    function group(n, decimals) {
      var fixed = n.toFixed(decimals);
      var parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }

    return (format || 'Rs.{{amount}}').replace(
      /\{\{\s*(\w+)\s*\}\}/,
      function (match, name) {
        switch (name) {
          case 'amount_no_decimals':
            return group(value, 0);
          case 'amount_with_comma_separator':
            return group(value, 2).replace(/[,.]/g, function (c) {
              return c === ',' ? '.' : ',';
            });
          case 'amount_no_decimals_with_comma_separator':
            return group(value, 0).replace(/,/g, '.');
          default:
            // money_without_trailing_zeros is what the template uses, so a
            // whole amount must not grow a ".00" the moment JS touches it.
            return value % 1 === 0 ? group(value, 0) : group(value, 2);
        }
      }
    );
  }

  function initCart(form) {
    var moneyFormat = form.dataset.moneyFormat || '';

    // Delivery is a two-tier weight rate: a base charge below the threshold and
    // a heavier one at or above it. Both figures come from theme settings and
    // have to match Settings → Shipping. Holding both tiers here rather than one
    // already-resolved number is what lets the total correct itself the instant
    // a quantity change tips the cart over the threshold — otherwise the cart
    // would quote a delivery charge the checkout is not going to apply.
    var deliveryBase = parseInt(form.dataset.delivery, 10) || 0;
    var deliveryHeavy = parseInt(form.dataset.deliveryHeavy, 10) || 0;
    var deliveryBaseNote = form.dataset.deliveryNote || '';
    var deliveryHeavyNote = form.dataset.deliveryHeavyNote || '';
    var weightThreshold = parseInt(form.dataset.weightThreshold, 10) || 0;

    var subtotalEl = form.querySelector('[data-arc-subtotal]');
    var totalEl = form.querySelector('[data-arc-total]');
    var deliveryEl = form.querySelector('[data-arc-delivery]');
    var deliveryNoteEl = form.querySelector('[data-arc-delivery-note]');

    // One request at a time, newest intent wins. Clicking + five times fast
    // sends the fifth quantity, not five racing requests that can land out of
    // order and leave the cart on whichever one happened to finish last.
    var chain = Promise.resolve();
    var inFlight = 0;

    function lines() {
      return Array.prototype.slice.call(form.querySelectorAll('[data-arc-line]'));
    }

    function repaintTotals() {
      var subtotal = 0;
      var weight = 0;
      lines().forEach(function (line) {
        var val = line.querySelector('[data-arc-qty-val]');
        if (!val) return;
        var qty = parseInt(val.dataset.quantity, 10) || 0;
        var unit = parseInt(val.dataset.unitPrice, 10) || 0;
        var unitWeight = parseInt(val.dataset.unitWeight, 10) || 0;
        subtotal += qty * unit;
        weight += qty * unitWeight;
      });

      // A threshold of 0 means "one rate for every order" — never treat that as
      // every cart being over the line.
      var heavy = weightThreshold > 0 && weight >= weightThreshold;
      var delivery = heavy ? deliveryHeavy : deliveryBase;

      if (subtotalEl) subtotalEl.textContent = formatMoney(subtotal, moneyFormat);
      if (deliveryEl) deliveryEl.textContent = formatMoney(delivery, moneyFormat);
      if (deliveryNoteEl) {
        deliveryNoteEl.textContent = heavy ? deliveryHeavyNote : deliveryBaseNote;
      }
      if (totalEl) totalEl.textContent = formatMoney(subtotal + delivery, moneyFormat);
    }

    function paintLine(line, qty) {
      var val = line.querySelector('[data-arc-qty-val]');
      var priceEl = line.querySelector('[data-arc-line-price]');
      if (!val) return;

      val.dataset.quantity = String(qty);
      val.textContent = String(qty);

      var unit = parseInt(val.dataset.unitPrice, 10) || 0;
      if (priceEl) priceEl.textContent = formatMoney(qty * unit, moneyFormat);

      // Keep the no-JS hrefs pointing at the right next step.
      var minus = line.querySelector('[data-arc-qty="-1"]');
      var plus = line.querySelector('[data-arc-qty="1"]');
      [[minus, qty - 1], [plus, qty + 1]].forEach(function (pair) {
        if (!pair[0]) return;
        pair[0].href = pair[0].href.replace(
          /quantity=\d+/,
          'quantity=' + Math.max(0, pair[1])
        );
      });

      repaintTotals();
    }

    function reconcile(cart) {
      // The server is the authority: stock limits, a line that vanished, a
      // quantity clamped below what was asked for.
      if (!cart || typeof cart.item_count !== 'number') return;
      if (cart.item_count === 0) {
        window.location.reload();
        return;
      }

      var byKey = {};
      (cart.items || []).forEach(function (item) {
        byKey[item.key] = item;
      });

      lines().forEach(function (line) {
        var item = byKey[line.dataset.arcKey];
        if (!item) {
          line.remove();
          return;
        }
        var val = line.querySelector('[data-arc-qty-val]');
        if (val) val.dataset.unitPrice = String(item.final_price);
        paintLine(line, item.quantity);
      });

      repaintTotals();
    }

    function send(key, quantity) {
      form.dataset.arcBusy = 'true';
      inFlight++;

      chain = chain
        .then(function () {
          return fetch(window.Shopify && window.Shopify.routes
            ? window.Shopify.routes.root + 'cart/change.js'
            : '/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({ id: key, quantity: quantity })
          });
        })
        .then(function (res) {
          return res.ok ? res.json() : null;
        })
        .then(function (cart) {
          if (cart) reconcile(cart);
        })
        .catch(function () {
          // Network gone or Shopify unhappy — fall back to the real navigation
          // so the visitor is never left looking at a number that lied.
          window.location.reload();
        })
        .then(function () {
          inFlight--;
          if (inFlight === 0) form.removeAttribute('data-arc-busy');
        });
    }

    form.addEventListener('click', function (e) {
      var control = e.target.closest('[data-arc-qty], [data-arc-remove]');
      if (!control || !form.contains(control)) return;

      var line = control.closest('[data-arc-line]');
      var val = line && line.querySelector('[data-arc-qty-val]');
      if (!line || !val) return;

      var current = parseInt(val.dataset.quantity, 10) || 0;
      var next;

      if (control.hasAttribute('data-arc-remove')) {
        next = 0;
      } else {
        next = current + (parseInt(control.dataset.arcQty, 10) || 0);
      }

      if (next < 0) next = 0;
      if (next === current) {
        e.preventDefault();
        return;
      }

      e.preventDefault();

      if (next === 0) {
        // Nothing optimistic about removal — the row going before the server
        // has agreed is jarring if the request then fails.
        line.dataset.arcRemoving = 'true';
      } else {
        paintLine(line, next);
      }

      send(line.dataset.arcKey, next);
    });
  }

  /* ---------------------------------------------------------------------
     Back link

     Goes back to wherever the visitor came from, but only when that was a page
     on this store — otherwise (a bookmark, a shared link, a search result) it
     falls through to the href already on the element.
     --------------------------------------------------------------------- */

  function initBack() {
    var links = document.querySelectorAll('[data-arc-back]');
    if (!links.length) return;

    var cameFromHere =
      document.referrer &&
      document.referrer.indexOf(window.location.origin) === 0 &&
      document.referrer !== window.location.href;

    Array.prototype.forEach.call(links, function (link) {
      if (!cameFromHere) return;
      link.addEventListener('click', function (e) {
        e.preventDefault();
        window.history.back();
      });
    });
  }

  /* ---------------------------------------------------------------------
     Phone menu drawer

     The opener is a real <a href="#ArcMenu"> and CSS opens the drawer from
     .menu:target, so the menu works with JavaScript off. This stamps
     data-ready on the drawer — which hands control to data-open — and adds
     the four things CSS cannot do: a scroll lock, Escape, a focus loop, and
     closing when the viewport crosses into the desktop layout.

     There is no cached `open` boolean anywhere here. The data-open attribute
     IS the state and every check reads it live, for the same reason the
     carousel has no cached hover flag: a boolean kept in sync by handlers
     wedges the moment one event goes missing.
     --------------------------------------------------------------------- */

  function initMenu() {
    var menu = document.querySelector('[data-arc-menu]');
    if (!menu) return;
    // Already wired. A theme-editor section reload replaces the element, so a
    // genuine re-render still gets its listeners; this only stops them piling
    // up on the same one.
    if (menu.dataset.ready === 'true') return;

    var panel = menu.querySelector('[data-arc-menu-panel]');
    var openers = document.querySelectorAll('[data-arc-menu-open]');
    var closers = menu.querySelectorAll('[data-arc-menu-close]');
    var lastFocus = null;

    // Hands the open/closed decision over from :target to data-open.
    menu.dataset.ready = 'true';
    menu.dataset.open = 'false';

    function isOpen() {
      return menu.dataset.open === 'true';
    }

    function setExpanded(v) {
      Array.prototype.forEach.call(openers, function (o) {
        o.setAttribute('aria-expanded', v ? 'true' : 'false');
      });
    }

    function open(e) {
      if (e) e.preventDefault();
      lastFocus = e && e.currentTarget ? e.currentTarget : null;
      menu.dataset.open = 'true';
      document.body.style.overflow = 'hidden';
      setExpanded(true);
      var first = menu.querySelector('[data-arc-menu-close]:not([tabindex="-1"])');
      if (first) first.focus();
    }

    // restoreFocus is false when the drawer is closing because the visitor is
    // navigating away — pulling focus back to the hamburger there fights the
    // browser's own scroll to the anchor.
    function close(restoreFocus) {
      if (!isOpen()) return;
      menu.dataset.open = 'false';
      document.body.style.overflow = '';
      setExpanded(false);
      if (restoreFocus && lastFocus) lastFocus.focus();
    }

    Array.prototype.forEach.call(openers, function (btn) {
      btn.addEventListener('click', open);
    });

    Array.prototype.forEach.call(closers, function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        close(true);
      });
    });

    // A link inside the panel closes it. Same-page anchors would otherwise
    // scroll behind a drawer that is still covering the page.
    panel.addEventListener('click', function (e) {
      var el = e.target;
      while (el && el !== panel && el.tagName !== 'A') el = el.parentNode;
      if (!el || el.tagName !== 'A') return;
      if (el.hasAttribute('data-arc-menu-close')) return;
      close(false);
    });

    document.addEventListener('keydown', function (e) {
      if (!isOpen()) return;

      if (e.key === 'Escape') {
        close(true);
        return;
      }

      if (e.key !== 'Tab') return;

      // Focus loop. The drawer covers the page, so without this Tab walks off
      // into the links behind the scrim. The scrim itself is tabindex="-1" and
      // sits outside the panel, so it is never in this list.
      var f = panel.querySelectorAll('a[href], button:not([disabled])');
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    // Resizing past the breakpoint hides the drawer in CSS. Without this the
    // body scroll lock would survive it and the page would be stuck.
    var wide = window.matchMedia('(min-width: 861px)');
    var onWide = function () {
      if (wide.matches) close(false);
    };
    if (wide.addEventListener) wide.addEventListener('change', onWide);
    else if (wide.addListener) wide.addListener(onWide);
  }

  /* ---------------------------------------------------------------------
     Modals
     --------------------------------------------------------------------- */

  function initModals() {
    var openers = document.querySelectorAll('[data-arc-open-modal]');
    if (!openers.length) return;

    var lastFocus = null;

    function close(modal) {
      modal.hidden = true;
      document.body.style.overflow = '';
      if (lastFocus) lastFocus.focus();
    }

    Array.prototype.forEach.call(openers, function (btn) {
      btn.addEventListener('click', function () {
        var modal = document.getElementById(btn.dataset.arcOpenModal);
        if (!modal) return;
        lastFocus = btn;
        modal.hidden = false;
        document.body.style.overflow = 'hidden';
        var closer = modal.querySelector('[data-arc-close-modal]');
        if (closer) closer.focus();
      });
    });

    Array.prototype.forEach.call(
      document.querySelectorAll('[data-arc-modal]'),
      function (modal) {
        modal.addEventListener('click', function (e) {
          // Backdrop click only — clicks inside the panel shouldn't close it.
          if (!e.target.closest('[data-arc-modal-panel]')) close(modal);
        });

        var closer = modal.querySelector('[data-arc-close-modal]');
        if (closer) {
          closer.addEventListener('click', function () {
            close(modal);
          });
        }
      }
    );

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      Array.prototype.forEach.call(
        document.querySelectorAll('[data-arc-modal]'),
        function (modal) {
          if (!modal.hidden) close(modal);
        }
      );
    });
  }

  /* ---------------------------------------------------------------------
     Description tabs
     --------------------------------------------------------------------- */

  function initTabs() {
    var tabs = Array.prototype.slice.call(document.querySelectorAll('[data-arc-dtab]'));
    if (!tabs.length) return;

    var panels = Array.prototype.slice.call(
      document.querySelectorAll('[data-arc-dpanel]')
    );

    function select(tab) {
      tabs.forEach(function (t) {
        t.setAttribute('aria-selected', String(t === tab));
      });
      panels.forEach(function (p) {
        p.hidden = p.id !== tab.getAttribute('aria-controls');
      });
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () {
        select(tab);
      });

      tab.addEventListener('keydown', function (e) {
        var next = null;
        if (e.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
        if (e.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
        if (!next) return;
        e.preventDefault();
        next.focus();
        select(next);
      });
    });
  }

  /* ---------------------------------------------------------------------
     FAQ accordion
     --------------------------------------------------------------------- */

  function initFaq(root) {
    var buttons = Array.prototype.slice.call(root.querySelectorAll('.faq__q'));

    function paint(openBtn) {
      buttons.forEach(function (btn) {
        var item = btn.closest('.faq__item');
        var on = btn === openBtn;
        btn.setAttribute('aria-expanded', String(on));
        if (item) item.dataset.open = String(on);
      });
    }

    // Taking over from the CSS default (all answers open, for the no-JS case).
    root.dataset.arcFaqReady = 'true';

    var initiallyOpen = buttons.filter(function (btn) {
      return btn.getAttribute('aria-expanded') === 'true';
    })[0];
    paint(initiallyOpen || null);

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var isOpen = btn.getAttribute('aria-expanded') === 'true';
        paint(isOpen ? null : btn);
      });
    });
  }

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */

  // The heartbeat runs for the life of the page, so a carousel that gets
  // rebuilt (theme editor re-render) must have its old instance torn down or
  // the two fight over the same slides at different phases.
  function mountCarousel(el) {
    if (el.arcCarousel && el.arcCarousel.destroy) el.arcCarousel.destroy();
    el.arcCarousel = new ArcCarousel(el);
  }

  function init() {
    document.querySelectorAll('[data-arc-carousel]').forEach(mountCarousel);

    // Product page: same variant engine, driven by swatches instead of a
    // carousel.
    document.querySelectorAll('[data-arc-product]').forEach(function (el) {
      new ArcVariants(el);
    });

    document.querySelectorAll('[data-arc-faq]').forEach(initFaq);
    document.querySelectorAll('[data-arc-cart]').forEach(initCart);
    initMedia();
    initMenu();
    initModals();
    initTabs();
    initBack();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('[data-arc-carousel]');
    if (el) mountCarousel(el);
    var pdp = e.target.querySelector('[data-arc-product]');
    if (pdp) new ArcVariants(pdp);
    var faq = e.target.querySelector('[data-arc-faq]');
    if (faq) initFaq(faq);
    initMedia();
    initMenu();
    initModals();
    initTabs();
    initBack();
  });
})();
